// Translate validation errors returned by `fiscalCodeMulti.js` validators
// into the customer's UI language. The validators return Italian strings
// for historical reasons; this module pattern-matches them and emits the
// localised equivalent.
//
// The validators use ~8 recurring shapes:
//   "Il X deve essere di N cifre"
//   "Il X deve essere di N caratteri"
//   "Formato X non valido"
//   "Carattere di controllo non valido (Codice Fiscale errato)"
//   "Checksum X non valido"
//   "Chiave di controllo del X non valida"
//   "Prefisso X non valido"
//   "X non valido"
// Anything we don't recognise is returned verbatim (already in IT).

const PATTERNS = [
  // "Il NIR francese deve essere di 13 o 15 cifre"
  { re: /^Il (.+?) deve essere di (.+?) cifre$/i,
    fmt: (m, t) => t.must_be_digits.replace('{doc}', m[1]).replace('{n}', m[2]) },
  // "Il NIF deve essere di 9 caratteri (8 cifre + 1 lettera)"
  { re: /^(?:Il |Lo |La )(.+?) deve essere di (.+?) (?:caratteri|caratteri\b.*)$/i,
    fmt: (m, t) => t.must_be_chars.replace('{doc}', m[1]).replace('{n}', m[2]) },
  // "Lo Steuer-ID deve essere di 11 cifre"  → handled by first rule with strip
  // "Formato X non valido"
  { re: /^Formato (.+?) non valido$/i,
    fmt: (m, t) => t.format_invalid.replace('{doc}', m[1]) },
  // "Carattere di controllo non valido (Codice Fiscale errato)"
  { re: /^Carattere di controllo non valido \(Codice Fiscale errato\)$/i,
    fmt: (m, t) => t.check_char_invalid_cf },
  // "Checksum X non valido"
  { re: /^Checksum (.+?) non valido$/i,
    fmt: (m, t) => t.checksum_invalid.replace('{doc}', m[1]) },
  // "Chiave di controllo del X non valida"
  { re: /^Chiave di controllo (?:del |della )?(.+?) non valida$/i,
    fmt: (m, t) => t.control_key_invalid.replace('{doc}', m[1]) },
  // "Prefisso X non valido"
  { re: /^Prefisso (.+?) non valido$/i,
    fmt: (m, t) => t.prefix_invalid.replace('{doc}', m[1]) },
  // VAT specific
  { re: /^Formato Partita IVA non valido$/i,
    fmt: (m, t) => t.vat_format_invalid },
  { re: /^P\.IVA non valida \(checksum\)$/i,
    fmt: (m, t) => t.vat_checksum_invalid },
  // Tax code generic catch-all
  { re: /^(.+?) non valido (?:\(.+?\))?$/i,
    fmt: (m, t) => t.generic_invalid.replace('{doc}', m[1]) },
];

const TRANSLATIONS = {
  IT: {
    must_be_digits: 'Il {doc} deve essere di {n} cifre',
    must_be_chars: 'Il {doc} deve essere di {n} caratteri',
    format_invalid: 'Formato {doc} non valido',
    check_char_invalid_cf: 'Carattere di controllo non valido (Codice Fiscale errato)',
    checksum_invalid: 'Checksum {doc} non valido',
    control_key_invalid: 'Chiave di controllo del {doc} non valida',
    prefix_invalid: 'Prefisso {doc} non valido',
    vat_format_invalid: 'Formato P.IVA non valido',
    vat_checksum_invalid: 'P.IVA non valida (checksum)',
    generic_invalid: '{doc} non valido',
  },
  UK: {
    must_be_digits: '{doc} must be {n} digits',
    must_be_chars: '{doc} must be {n} characters',
    format_invalid: 'Invalid {doc} format',
    check_char_invalid_cf: 'Invalid check character (Tax Code incorrect)',
    checksum_invalid: 'Invalid {doc} checksum',
    control_key_invalid: 'Invalid {doc} control key',
    prefix_invalid: 'Invalid {doc} prefix',
    vat_format_invalid: 'Invalid VAT format',
    vat_checksum_invalid: 'Invalid VAT number (checksum)',
    generic_invalid: 'Invalid {doc}',
  },
  FR: {
    must_be_digits: 'Le {doc} doit avoir {n} chiffres',
    must_be_chars: 'Le {doc} doit avoir {n} caracteres',
    format_invalid: 'Format {doc} invalide',
    check_char_invalid_cf: 'Caractere de controle invalide (Code fiscal incorrect)',
    checksum_invalid: 'Checksum {doc} invalide',
    control_key_invalid: 'Cle de controle du {doc} invalide',
    prefix_invalid: 'Prefixe {doc} invalide',
    vat_format_invalid: 'Format TVA invalide',
    vat_checksum_invalid: 'Numero TVA invalide (checksum)',
    generic_invalid: '{doc} invalide',
  },
  ES: {
    must_be_digits: 'El {doc} debe tener {n} digitos',
    must_be_chars: 'El {doc} debe tener {n} caracteres',
    format_invalid: 'Formato {doc} no valido',
    check_char_invalid_cf: 'Caracter de control no valido (Codigo Fiscal incorrecto)',
    checksum_invalid: 'Checksum {doc} no valido',
    control_key_invalid: 'Clave de control del {doc} no valida',
    prefix_invalid: 'Prefijo {doc} no valido',
    vat_format_invalid: 'Formato IVA no valido',
    vat_checksum_invalid: 'Numero IVA no valido (checksum)',
    generic_invalid: '{doc} no valido',
  },
  DE: {
    must_be_digits: 'Die {doc} muss {n} Ziffern haben',
    must_be_chars: 'Die {doc} muss {n} Zeichen haben',
    format_invalid: 'Ungultiges {doc}-Format',
    check_char_invalid_cf: 'Ungultiges Prufzeichen (Steuernummer falsch)',
    checksum_invalid: 'Ungultige {doc}-Prufsumme',
    control_key_invalid: 'Ungultiger {doc}-Prufschlussel',
    prefix_invalid: 'Ungultiges {doc}-Prafix',
    vat_format_invalid: 'Ungultiges USt-Format',
    vat_checksum_invalid: 'Ungultige USt-IdNr. (Prufsumme)',
    generic_invalid: 'Ungultige {doc}',
  },
  NL: {
    must_be_digits: 'De {doc} moet {n} cijfers hebben',
    must_be_chars: 'De {doc} moet {n} tekens hebben',
    format_invalid: 'Ongeldig {doc}-formaat',
    check_char_invalid_cf: 'Ongeldig controlekarakter (Belastingnummer onjuist)',
    checksum_invalid: 'Ongeldige {doc}-checksum',
    control_key_invalid: 'Ongeldige {doc}-controlesleutel',
    prefix_invalid: 'Ongeldig {doc}-voorvoegsel',
    vat_format_invalid: 'Ongeldig BTW-formaat',
    vat_checksum_invalid: 'Ongeldig BTW-nummer (checksum)',
    generic_invalid: 'Ongeldige {doc}',
  },
  PT: {
    must_be_digits: 'O {doc} deve ter {n} digitos',
    must_be_chars: 'O {doc} deve ter {n} caracteres',
    format_invalid: 'Formato {doc} invalido',
    check_char_invalid_cf: 'Caractere de controlo invalido (Codigo Fiscal incorrecto)',
    checksum_invalid: 'Checksum {doc} invalido',
    control_key_invalid: 'Chave de controlo do {doc} invalida',
    prefix_invalid: 'Prefixo {doc} invalido',
    vat_format_invalid: 'Formato IVA invalido',
    vat_checksum_invalid: 'Numero IVA invalido (checksum)',
    generic_invalid: '{doc} invalido',
  },
  PL: {
    must_be_digits: '{doc} musi miec {n} cyfr',
    must_be_chars: '{doc} musi miec {n} znakow',
    format_invalid: 'Nieprawidlowy format {doc}',
    check_char_invalid_cf: 'Nieprawidlowy znak kontrolny (Numer podatkowy bledny)',
    checksum_invalid: 'Nieprawidlowa suma kontrolna {doc}',
    control_key_invalid: 'Nieprawidlowy klucz kontrolny {doc}',
    prefix_invalid: 'Nieprawidlowy prefiks {doc}',
    vat_format_invalid: 'Nieprawidlowy format VAT',
    vat_checksum_invalid: 'Nieprawidlowy numer VAT (suma kontrolna)',
    generic_invalid: 'Nieprawidlowe {doc}',
  },
  SE: {
    must_be_digits: '{doc} maste vara {n} siffror',
    must_be_chars: '{doc} maste vara {n} tecken',
    format_invalid: 'Ogiltigt {doc}-format',
    check_char_invalid_cf: 'Ogiltigt kontrolltecken (Skattenummer felaktigt)',
    checksum_invalid: 'Ogiltig {doc}-kontrollsumma',
    control_key_invalid: 'Ogiltig {doc}-kontrollnyckel',
    prefix_invalid: 'Ogiltigt {doc}-prefix',
    vat_format_invalid: 'Ogiltigt momsformat',
    vat_checksum_invalid: 'Ogiltigt momsnummer (kontrollsumma)',
    generic_invalid: 'Ogiltig {doc}',
  },
  NO: {
    must_be_digits: '{doc} ma vaere {n} sifre',
    must_be_chars: '{doc} ma vaere {n} tegn',
    format_invalid: 'Ugyldig {doc}-format',
    check_char_invalid_cf: 'Ugyldig kontrolltegn (Skattenummer feil)',
    checksum_invalid: 'Ugyldig {doc}-kontrollsum',
    control_key_invalid: 'Ugyldig {doc}-kontrollnokkel',
    prefix_invalid: 'Ugyldig {doc}-prefiks',
    vat_format_invalid: 'Ugyldig MVA-format',
    vat_checksum_invalid: 'Ugyldig MVA-nummer (kontrollsum)',
    generic_invalid: 'Ugyldig {doc}',
  },
  FI: {
    must_be_digits: '{doc} on oltava {n} numeroa',
    must_be_chars: '{doc} on oltava {n} merkkia',
    format_invalid: 'Virheellinen {doc}-muoto',
    check_char_invalid_cf: 'Virheellinen tarkistusmerkki (Verotunnus virheellinen)',
    checksum_invalid: 'Virheellinen {doc}-tarkistussumma',
    control_key_invalid: 'Virheellinen {doc}-tarkistusavain',
    prefix_invalid: 'Virheellinen {doc}-etuliite',
    vat_format_invalid: 'Virheellinen ALV-muoto',
    vat_checksum_invalid: 'Virheellinen ALV-numero (tarkistussumma)',
    generic_invalid: 'Virheellinen {doc}',
  },
  DK: {
    must_be_digits: '{doc} skal vaere {n} cifre',
    must_be_chars: '{doc} skal vaere {n} tegn',
    format_invalid: 'Ugyldigt {doc}-format',
    check_char_invalid_cf: 'Ugyldigt kontroltegn (Skattenummer forkert)',
    checksum_invalid: 'Ugyldig {doc}-kontrolsum',
    control_key_invalid: 'Ugyldig {doc}-kontrolnogle',
    prefix_invalid: 'Ugyldigt {doc}-praefix',
    vat_format_invalid: 'Ugyldigt momsformat',
    vat_checksum_invalid: 'Ugyldigt momsnummer (kontrolsum)',
    generic_invalid: 'Ugyldig {doc}',
  },
  CZ: {
    must_be_digits: '{doc} musi mit {n} cislic',
    must_be_chars: '{doc} musi mit {n} znaku',
    format_invalid: 'Neplatny format {doc}',
    check_char_invalid_cf: 'Neplatny kontrolni znak (Danove cislo nespravne)',
    checksum_invalid: 'Neplatny kontrolni soucet {doc}',
    control_key_invalid: 'Neplatny kontrolni klic {doc}',
    prefix_invalid: 'Neplatny prefix {doc}',
    vat_format_invalid: 'Neplatny format DPH',
    vat_checksum_invalid: 'Neplatne DIC (kontrolni soucet)',
    generic_invalid: 'Neplatne {doc}',
  },
  GR: {
    must_be_digits: 'To {doc} prepei na einai {n} psifia',
    must_be_chars: 'To {doc} prepei na einai {n} charaktires',
    format_invalid: 'Mi egyro morfi {doc}',
    check_char_invalid_cf: 'Mi egyros charaktiras elenchou (Forologikos kodikas lathos)',
    checksum_invalid: 'Mi egyro athrismatos elenchou {doc}',
    control_key_invalid: 'Mi egyro kleidi elenchou tou {doc}',
    prefix_invalid: 'Mi egyro prothima {doc}',
    vat_format_invalid: 'Mi egyri morfi FPA',
    vat_checksum_invalid: 'Mi egyros arithmos FPA (athrismatos elenchou)',
    generic_invalid: 'Mi egyro {doc}',
  },
};
TRANSLATIONS.CH = TRANSLATIONS.DE;
TRANSLATIONS.GBZ = TRANSLATIONS.UK;
TRANSLATIONS.IRL = TRANSLATIONS.UK;
TRANSLATIONS.BE = TRANSLATIONS.FR;

/** Translate an Italian validation error into the customer's UI language. */
export const translateValidationError = (errorMsg, langCode = 'IT') => {
  if (!errorMsg) return errorMsg;
  const t = TRANSLATIONS[(langCode || 'IT').toUpperCase()] || TRANSLATIONS.IT;
  for (const { re, fmt } of PATTERNS) {
    const m = errorMsg.match(re);
    if (m) {
      try { return fmt(m, t); }
      catch { return errorMsg; }
    }
  }
  // Unknown shape — return the original italian (better than a missing string).
  return errorMsg;
};

export default translateValidationError;
